import type {
  AudioFormArrangement,
  AudioFormAssets,
  AudioFormClip,
  AudioFormInput,
  AudioFormSection,
  SoundDesignSnippetAsset,
  StsScriptAsset,
  TtsScriptAsset,
} from '../../../../audio-form/data/dto/audio-form-input.ts'
import { mapMediaFileLayerToClips } from './map-media-file-layer-to-clips.ts'
import {
  mapTtsSpeechLayerToClips,
  mapStsSpeechLayerToClips,
} from './map-voices-to-clips.ts'
import { mapSoundEffectLayerToClips } from './map-sound-effect-layer-to-clips.ts'
import { useHistoryStore } from '@/shared/store/history.store.tsx'
import { refreshAudioMix } from '@/shared/store/use-cases/refresh-audio-mix.ts'
import { mapSoundDesignLayerToClips } from './map-sound-design-layer-to-clips.ts'
import useTracksStore, {
  SOUND_DESIGN_TRACK_ID,
} from '@/shared/store/tracks.store.tsx'
import { mapSoundDesignSectionToClip } from './map-sound-design-section-to-clip.ts'
import { mapSpeechesToClips } from './map-speeches-to-clips.ts'

const loadTtsClip = async (
  asset: TtsScriptAsset,
  clip: AudioFormClip,
  assets: AudioFormAssets
): Promise<void> => {
  const voiceAsset = assets[asset.voiceRef]
  if (voiceAsset?.type !== 'voice') {
    return
  }
  await mapTtsSpeechLayerToClips(asset, voiceAsset, clip)
}

const loadStsClip = async (
  asset: StsScriptAsset,
  clip: AudioFormClip,
  assets: AudioFormAssets
): Promise<void> => {
  const voiceAsset = assets[asset.voiceRef]
  if (voiceAsset?.type !== 'voice') {
    return
  }
  await mapStsSpeechLayerToClips(asset, voiceAsset, clip)
}

const loadSoundTemplateSnippetClip = async (
  asset: SoundDesignSnippetAsset,
  clip: AudioFormClip,
  assets: AudioFormAssets
): Promise<void> => {
  const template = assets[asset.soundTemplateRef]
  if (template?.type !== 'soundTemplate') {
    return
  }
  await mapSoundDesignLayerToClips(template, clip)
}

const loadSoundTemplateClip = async (
  section: AudioFormSection,
  arrangement: AudioFormArrangement,
  assets: AudioFormAssets
): Promise<void> => {
  if (
    section.soundTemplateRef &&
    assets[section.soundTemplateRef]?.type === 'soundTemplate'
  ) {
    const soundDesignAsset = assets[section.soundTemplateRef]
    if (soundDesignAsset?.type !== 'soundTemplate') {
      return
    }

    await mapSoundDesignSectionToClip(soundDesignAsset, section, arrangement)
  }
}

const updateStore = async (promises: Array<Promise<void>>): Promise<void> => {
  const results = await Promise.allSettled(promises)

  const rejected = results.filter(
    (r): r is PromiseRejectedResult => r.status === 'rejected'
  )

  if (rejected.length > 0) {
    console.error(`${rejected.length} clips couldn't be loaded`)
    rejected.forEach((r) => {
      console.error(r.reason)
    })
  }
}

const handleSmartFit = (section?: AudioFormSection): void => {
  if (section?.smartFit) {
    useTracksStore
      .getState()
      .updateTrack(SOUND_DESIGN_TRACK_ID, { smartFit: true })
  }
}

export const mapAudioFormToTracksStore = async (
  audioForm: AudioFormInput
): Promise<void> => {
  const arrangement = audioForm.production.arrangement
  const section = arrangement.sections[0]
  const tracks = section?.layers ?? []
  const assets = audioForm.assets
  let mediaTrackCount = 0

  handleSmartFit(section)

  const promises: Array<Promise<void>> = []

  for (const track of tracks) {
    const hasMediaClip = track.clips.some((clip) => {
      const asset = assets[clip.assetRef]
      return asset?.type === 'media'
    })

    let trackId: string | undefined = undefined
    if (hasMediaClip) {
      mediaTrackCount++
      if (mediaTrackCount > 1) {
        trackId = useTracksStore.getState().addMediaFileTrack()
      }
    }

    for (const clip of track.clips) {
      const asset = assets[clip.assetRef]
      switch (asset?.type) {
        case 'media':
          promises.push(mapMediaFileLayerToClips(asset, clip, trackId))
          break
        case 'tts':
          promises.push(loadTtsClip(asset, clip, assets))
          break
        case 'sts':
          promises.push(loadStsClip(asset, clip, assets))
          break
        case 'speech':
          promises.push(
            // todo: hack because the backend is not working well
            mapSpeechesToClips({ ...asset, speechId: clip.assetRef }, clip)
          )
          break
        case 'soundTemplateSnippet':
          promises.push(loadSoundTemplateSnippetClip(asset, clip, assets))
          break
        case 'soundEffect':
          promises.push(mapSoundEffectLayerToClips(asset, clip))
          break
        default:
          break
      }
    }
  }

  if (section) {
    promises.push(loadSoundTemplateClip(section, arrangement, assets))
  }

  await updateStore(promises)

  useHistoryStore.getState().saveSnapshot()
  await refreshAudioMix(false)
}
